import { defineStore } from 'pinia';
import router from '@/router/router';
import { hasSimulatedTourSaved, useGuidedTourStore } from '../GuidedTour/guidedTourStore';
import { closeTutorialGateway, openTutorialGateway } from './tutorialGateway';

/**
 * O tutorial: o uso guiado rodando sobre as telas reais com o roteiro de treino.
 *
 * A diferença em relação ao guia sobre dados reais: nada do que a pessoa faz sai desta
 * máquina. É isso que permite ao tutorial pedir para ir até o fim, inclusive criar a
 * ocorrência — a etapa que dá medo é justamente a que ela precisa praticar.
 *
 * Sair está sempre disponível e devolve a pessoa às boas-vindas. Também há o
 * encerramento por falha: se o portão barrar algo fora do roteiro, o tutorial termina
 * sozinho com uma mensagem honesta, em vez de deixar alguém preso por defeito nosso.
 */

export const useTutorialStore = defineStore('tutorial', {
	state: () => ({
		running: false,
		/** Preenchido quando o tutorial se encerra por conta própria. */
		failure: null as string | null,
		/** Para onde voltar ao terminar. */
		returnTo: '/',
	}),

	actions: {
		async start() {
			if (this.running) return;

			this.failure = null;
			this.returnTo = router.currentRoute.value.fullPath;
			this.running = true;

			openTutorialGateway({ onFailure: (reason) => this.failWith(reason) });

			const tour = useGuidedTourStore();
			await tour.startOccurrenceTour('NOVA', { simulated: true });
		},

		/**
		 * Retoma o tutorial depois de um F5.
		 *
		 * A ordem importa: o portão abre ANTES de o tour voltar. Entre remontar o roteiro e
		 * fechar a rede não pode haver instante nenhum — a primeira tela do app já dispara
		 * requisição, e uma delas passando adiante é exatamente o que o portão existe para
		 * impedir. Se nada havia para retomar, o portão não chega a abrir.
		 */
		async restore() {
			// A pergunta vem antes de qualquer efeito: na esmagadora maioria dos
			// carregamentos não há tutorial a retomar, e nesses o portão não pode chegar a
			// abrir nem por um instante.
			if (this.running || !hasSimulatedTourSaved()) return;

			this.failure = null;
			this.returnTo = '/';
			this.running = true;

			// O portão abre ANTES de o tour voltar: entre remontar o roteiro e fechar a rede
			// não pode haver instante nenhum, porque a tela que o tour reabre já dispara
			// requisição ao montar.
			openTutorialGateway({ onFailure: (reason) => this.failWith(reason) });

			if (await useGuidedTourStore().restore()) return;

			this.running = false;
			closeTutorialGateway();
		},

		/** Conclusão normal: a pessoa chegou ao fim do roteiro. */
		async finish() {
			await this.teardown();
		},

		failWith(reason: string) {
			this.failure = reason;
			void this.teardown();
		},

		async teardown() {
			if (!this.running) return;

			this.running = false;
			closeTutorialGateway();

			useGuidedTourStore().stop();

			try {
				await router.push(this.returnTo);
			} catch {
				// Navegação abortada não deve impedir o fim do tutorial — o importante já
				// aconteceu: o portão fechou e as chamadas reais voltaram a valer.
			}
		},

		dismissFailure() {
			this.failure = null;
		},
	},
});

export default useTutorialStore;
